import { defineComponent as _defineComponent } from 'vue'
import FormContainer from "../shared/FormContainer.vue"
import { ref, computed, PropType, watch, onMounted } from "vue"
import { Form } from "../shared/types"
import {
  SubscriptionPeriodTypeEnum,
  ProductPlanFragmentFragment,
  FindPromoCodeQuery,
  FindPromoCodeQueryQuery,
  SubscriptionInput,
  CohortPeriodTypeEnum,
} from "@/gql"
import { Theme } from "@/types"
import { THEMES } from "@/constants"
import { useLazyQuery } from "@vue/apollo-composable"
import Icon from "@/components/widgets/icons/Icon.vue"
import { useUtilities } from "@/hooks/useUtilities"
import { extend } from "vee-validate"
import StripeCardInput from "@/components/widgets/input/StripeCardInput.vue"


export default /*#__PURE__*/_defineComponent({
  __name: 'SubscriptionForm',
  props: {
  form: {
    type: Object as PropType<Form>,
    required: true,
  },
  isSubmitting: {
    type: Boolean,
    required: false,
  },
  theme: {
    type: Object as PropType<Theme>,
    default: () => ({
      primaryColor: THEMES.PERALTA_PRIMARY,
    }),
    required: false,
  },
  plans: {
    type: Array as PropType<ProductPlanFragmentFragment[]> | undefined,
    required: false,
  },
  loadingPlan: {
    type: Boolean,
    required: false,
  },
  selectedPlan: {
    type: Object as PropType<ProductPlanFragmentFragment> | null,
    required: false,
  },
},
  emits: ["complete", "gotoPrev", "change", "updateCardInput"],
  setup(__props, { emit }) {

const props = __props



const { moneyFormat } = useUtilities()

const subscriptionForm = ref<
  SubscriptionInput & {
    subscriptionPeriod: SubscriptionPeriodTypeEnum
  }
>({
  subscriptionPeriod: SubscriptionPeriodTypeEnum.Monthly,
  productPlanId: "",
  seatsUsageCount: 1,
  promoCode: "",
  cohortPeriod: CohortPeriodTypeEnum.Cohort_1,
})

const productPlans = ref<ProductPlanFragmentFragment[]>([])
const cachedProductPlans = ref<ProductPlanFragmentFragment[]>([])
const subscriptionPeriodSwitch = ref(false)
const promoCodeError = ref<string | null>(null)

const {
  load,
  loading: promoCodeLoading,
  result: promoCode,
  onResult: onPromoCodeQueryResult,
} = useLazyQuery<FindPromoCodeQueryQuery>(FindPromoCodeQuery)

onPromoCodeQueryResult((result) => {
  if (result?.data?.findPromoCode?.plan) {
    productPlans.value = [
      {
        ...result?.data.findPromoCode?.plan,
      },
    ]
    subscriptionForm.value.productPlanId = result.data.findPromoCode?.plan.prn
    return
  }

  if (result?.data && !result?.data?.findPromoCode) {
    promoCodeError.value = "*This Promo code does not exists "
  }
})



const observer = ref(null)

const onSubmit = async () => {
  const validity = await (observer.value as any)?.validateWithInfo()
  if (!validity.isValid) return

  emit("complete")
}

const getProductPlan = (id: string) => {
  return productPlans.value?.find((plan) => plan.prn === id)
}

const filterProductPlans = () => {
  return cachedProductPlans.value?.filter(
    (item) => item.subscriptionPeriod === subscriptionForm.value.subscriptionPeriod
  )
}
const updateProductPlans = () => {
  productPlans.value = filterProductPlans()
}

extend("minmax_value", {
  validate(value) {
    const plan = selectedProductPlan.value

    return plan
      ? value >= plan?.minimumSeats &&
          value <= (plan?.maximumSeats === 0 ? Infinity : plan?.maximumSeats)
      : false
  },
  message: (_, placeholders) => {
    const plan = selectedProductPlan.value

    return plan && placeholders._value_ < plan?.minimumSeats
      ? `Must be ${plan?.minimumSeats} or more`
      : `Must be less than ${plan?.maximumSeats}`
  },
})

watch(
  () => subscriptionForm.value.subscriptionPeriod,
  () => {
    if (promoCode.value?.findPromoCode) return
    updateProductPlans()
  }
)

const selectedProductPlan = computed(() => {
  return getProductPlan(subscriptionForm.value.productPlanId)
})

watch(
  subscriptionForm,
  (value) => {
    emit("change", {
      productPlanId: value.productPlanId,
      seatsUsageCount: value.seatsUsageCount,
      promoCode: value.promoCode,
      cohortPeriod: value.cohortPeriod,
    })
  },
  {
    deep: true,
  }
)

watch(
  () => subscriptionForm.value.subscriptionPeriod,
  () => {
    if (subscriptionForm.value.subscriptionPeriod === SubscriptionPeriodTypeEnum.OneOff) {
      subscriptionPeriodSwitch.value = true
    } else {
      subscriptionPeriodSwitch.value = false
    }
  }
)

const onSubmitPromoCode = () => {
  if (!subscriptionForm.value.promoCode) return

  verifyPromoCode()
}

const verifyPromoCode = () => {
  promoCodeError.value = null
  load(undefined, {
    code: subscriptionForm.value.promoCode,
  })
}

const onSubscriptionPeriodChange = (value: boolean) => {
  if (value) subscriptionForm.value.subscriptionPeriod = SubscriptionPeriodTypeEnum.OneOff
  else subscriptionForm.value.subscriptionPeriod = SubscriptionPeriodTypeEnum.Monthly
}

const onProductPlanChange = (prn: string) => {
  const pp = getProductPlan(prn)
  if (pp) subscriptionForm.value.subscriptionPeriod = pp.subscriptionPeriod
}

const planFee = computed(() => {
  const plan = getProductPlan(subscriptionForm.value.productPlanId)?.feesSubunit ?? 0
  return plan / 100
})

const totalBill = computed(() => {
  return (subscriptionForm.value.seatsUsageCount ?? 0) * planFee.value
})

const isPromoCodeApplied = computed(() => {
  return promoCode.value?.findPromoCode
})

const url = new URL(window.location.href)
const searchParams = new URLSearchParams(url.search)

const populateSubscriptionForm = () => {
  subscriptionForm.value = {
    ...subscriptionForm.value,
    ...props.form.newSubscriptionInput,
  }
}

watch(
  () => props.plans,
  () => {
    if (props.plans) {
      productPlans.value = props.plans as ProductPlanFragmentFragment[]
      cachedProductPlans.value = props.plans as ProductPlanFragmentFragment[]

      updateProductPlans()
    }
  },
  {
    deep: true,
    immediate: true,
  }
)

watch(
  () => props.selectedPlan,
  () => {
    if (props.selectedPlan) {
      subscriptionForm.value.subscriptionPeriod = props.selectedPlan.subscriptionPeriod
    }
  },
  {
    deep: true,
    immediate: true,
  }
)

const onSubmitCardInfo = (card: any) => {
  emit("updateCardInput", {
    stripeCardId: card.token.card.id,
    paymentToken: card.token.id,
    lastFour: card.token.card.last4,
    name: card.token.card.name,
    cardType: card.token.card.brand,
  })

  onSubmit()
}

const cohorts = computed(() => [
  {
    text: "Cohort 1: 15th May",
    value: CohortPeriodTypeEnum.Cohort_1,
  },
  {
    text: "Cohort 2: 22nd July",
    value: CohortPeriodTypeEnum.Cohort_2,
  },
  {
    text: "Cohort 3: 23rd September",
    value: CohortPeriodTypeEnum.Cohort_3,
  },
])

onMounted(() => {
  populateSubscriptionForm()

  if (searchParams.has("subscription_period") && searchParams.get("subscription_period")) {
    subscriptionForm.value.subscriptionPeriod = searchParams.get(
      "subscription_period"
    ) as SubscriptionPeriodTypeEnum
  }

  if (searchParams.has("promo_code") && searchParams.get("promo_code")) {
    subscriptionForm.value.promoCode = searchParams.get("promo_code") as string
    verifyPromoCode()
  }
})

return { __sfc: true,props, moneyFormat, subscriptionForm, productPlans, cachedProductPlans, subscriptionPeriodSwitch, promoCodeError, load, promoCodeLoading, promoCode, onPromoCodeQueryResult, emit, observer, onSubmit, getProductPlan, filterProductPlans, updateProductPlans, selectedProductPlan, onSubmitPromoCode, verifyPromoCode, onSubscriptionPeriodChange, onProductPlanChange, planFee, totalBill, isPromoCodeApplied, url, searchParams, populateSubscriptionForm, onSubmitCardInfo, cohorts, FormContainer, SubscriptionPeriodTypeEnum, Icon, StripeCardInput }
}

})